/**
 * CheckoutPIX Component
 *
 * Fluxo de checkout PIX:
 * 1. Pedir CPF do usuario
 * 2. POST /payments/checkout → receber QR Code
 * 3. Exibir QR Code + codigo para copiar
 * 4. Polling a cada 3s em GET /payments/payment/:id
 * 5. Ao is_paid=true → "Premium ativado!" e chamar onSuccess
 */

import { useState, useEffect, useRef, useCallback } from 'react'

// ========================================
// TYPES
// ========================================

interface CheckoutPIXProps {
  planId: string
  onSuccess: () => void
  onBack: () => void
}

type CheckoutStep = 'cpf' | 'qrcode' | 'success' | 'error'

interface CheckoutResponse {
  payment_id: string
  pix_qrcode_base64?: string
  pix_qrcode_text?: string
  status?: string
}

interface PaymentStatusResponse {
  is_paid: boolean
  status?: string
}

// ========================================
// HELPERS
// ========================================

const API_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL)
  || 'https://cnhpass-api-prod.bruno-chrisostomo.workers.dev'

const DEFAULT_PLAN_ID = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_DEFAULT_PLAN_ID)
  || ''

function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return localStorage.getItem('auth_token')
  } catch {
    return null
  }
}

function formatCPF(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11)
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`
}

function isValidCPF(cpf: string): boolean {
  const digits = cpf.replace(/\D/g, '')
  return digits.length === 11
}

// ========================================
// COMPONENT
// ========================================

export default function CheckoutPIX({ planId, onSuccess, onBack }: CheckoutPIXProps) {
  const [step, setStep] = useState<CheckoutStep>('cpf')
  const [cpf, setCpf] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [paymentId, setPaymentId] = useState<string | null>(null)
  const [qrcodeBase64, setQrcodeBase64] = useState<string | null>(null)
  const [qrcodeText, setQrcodeText] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Limpar polling ao desmontar
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }
    }
  }, [])

  // ========================================
  // CHECKOUT REQUEST
  // ========================================

  const handleSubmitCPF = useCallback(async () => {
    if (!isValidCPF(cpf)) {
      setError('CPF invalido. Digite os 11 digitos.')
      return
    }

    setIsSubmitting(true)
    setError(null)

    const token = getAuthToken()
    const resolvedPlanId = DEFAULT_PLAN_ID || planId

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const res = await fetch(`${API_URL}/payments/checkout`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          plan_id: resolvedPlanId,
          billing_type: 'PIX',
          cpf: cpf.replace(/\D/g, ''),
        }),
      })

      if (!res.ok) {
        const errorData = await res.json().catch((): null => null) as { message?: string } | null
        throw new Error(errorData?.message || `Erro ${res.status} ao criar pagamento`)
      }

      const data: CheckoutResponse = await res.json()

      setPaymentId(data.payment_id)
      setQrcodeBase64(data.pix_qrcode_base64 || null)
      setQrcodeText(data.pix_qrcode_text || null)
      setStep('qrcode')

      // Iniciar polling
      startPolling(data.payment_id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao processar pagamento')
      setStep('error')
    } finally {
      setIsSubmitting(false)
    }
  }, [cpf, planId])

  // ========================================
  // POLLING
  // ========================================

  const startPolling = useCallback((paymentIdParam: string) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
    }

    const token = getAuthToken()

    pollingRef.current = setInterval(async () => {
      try {
        const headers: Record<string, string> = {}
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }

        const res = await fetch(`${API_URL}/payments/payment/${paymentIdParam}`, {
          headers,
        })

        if (!res.ok) return

        const data: PaymentStatusResponse = await res.json()

        if (data.is_paid) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
          }
          setStep('success')
          // Aguardar 2s para o usuario ver a mensagem de sucesso
          setTimeout(() => {
            onSuccess()
          }, 2000)
        }
      } catch {
        // Silenciar erros de polling — continuar tentando
      }
    }, 3000)
  }, [onSuccess])

  // ========================================
  // COPY PIX CODE
  // ========================================

  const handleCopyPix = useCallback(async () => {
    if (!qrcodeText) return
    try {
      await navigator.clipboard.writeText(qrcodeText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback para browsers antigos
      const textarea = document.createElement('textarea')
      textarea.value = qrcodeText
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [qrcodeText])

  // ========================================
  // RENDER: Step CPF
  // ========================================

  if (step === 'cpf') {
    return (
      <div className="bg-zinc-900 rounded-2xl border border-white/10 p-6">
        {/* Back button */}
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-white/50 hover:text-white/80 transition-colors mb-4"
        >
          <span>&larr;</span> Voltar
        </button>

        <h3 className="text-lg font-bold text-white mb-1">Pagamento via PIX</h3>
        <p className="text-sm text-white/50 mb-6">
          Informe seu CPF para gerar o QR Code
        </p>

        {/* CPF Input */}
        <div className="mb-4">
          <label className="block text-xs text-white/40 mb-1.5">CPF</label>
          <input
            type="text"
            inputMode="numeric"
            value={formatCPF(cpf)}
            onChange={(e) => {
              setCpf(e.target.value.replace(/\D/g, ''))
              setError(null)
            }}
            placeholder="000.000.000-00"
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/20 focus:outline-none focus:border-green-500/50 focus:ring-1 focus:ring-green-500/30 transition-colors"
            maxLength={14}
          />
          {error && (
            <p className="text-xs text-red-400 mt-1.5">{error}</p>
          )}
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmitCPF}
          disabled={isSubmitting || cpf.replace(/\D/g, '').length < 11}
          className={`
            w-full py-3.5 rounded-xl font-bold text-sm transition-all
            ${isSubmitting || cpf.replace(/\D/g, '').length < 11
              ? 'bg-white/10 text-white/30 cursor-not-allowed'
              : 'bg-gradient-to-r from-green-500 to-cyan-500 text-black hover:from-green-400 hover:to-cyan-400 shadow-lg shadow-green-500/20'
            }
          `}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              Gerando QR Code...
            </span>
          ) : (
            'Gerar QR Code PIX'
          )}
        </button>
      </div>
    )
  }

  // ========================================
  // RENDER: Step QR Code
  // ========================================

  if (step === 'qrcode') {
    return (
      <div className="bg-zinc-900 rounded-2xl border border-white/10 p-6">
        {/* Back button */}
        <button
          onClick={() => {
            if (pollingRef.current) {
              clearInterval(pollingRef.current)
              pollingRef.current = null
            }
            onBack()
          }}
          className="flex items-center gap-1 text-sm text-white/50 hover:text-white/80 transition-colors mb-4"
        >
          <span>&larr;</span> Voltar
        </button>

        <h3 className="text-lg font-bold text-white mb-1 text-center">Escaneie o QR Code</h3>
        <p className="text-sm text-white/50 mb-6 text-center">
          Abra o app do seu banco e escaneie o codigo
        </p>

        {/* QR Code */}
        <div className="flex justify-center mb-6">
          {qrcodeBase64 ? (
            <div className="p-4 bg-white rounded-2xl">
              <img
                src={qrcodeBase64.startsWith('data:') ? qrcodeBase64 : `data:image/png;base64,${qrcodeBase64}`}
                alt="QR Code PIX"
                className="w-48 h-48"
              />
            </div>
          ) : (
            <div className="w-56 h-56 bg-white/5 rounded-2xl flex items-center justify-center border border-white/10">
              <span className="text-white/30 text-sm">QR Code indisponivel</span>
            </div>
          )}
        </div>

        {/* PIX Copy-Paste */}
        {qrcodeText && (
          <div className="mb-6">
            <label className="block text-xs text-white/40 mb-1.5">Ou copie o codigo PIX:</label>
            <div className="flex gap-2">
              <div className="flex-1 px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-xs text-white/60 truncate font-mono">
                {qrcodeText}
              </div>
              <button
                onClick={handleCopyPix}
                className={`
                  px-4 py-2.5 rounded-xl text-xs font-bold shrink-0 transition-all
                  ${copied
                    ? 'bg-green-500 text-black'
                    : 'bg-white/10 text-white hover:bg-white/20'
                  }
                `}
              >
                {copied ? 'Copiado!' : 'Copiar'}
              </button>
            </div>
          </div>
        )}

        {/* Polling indicator */}
        <div className="flex items-center justify-center gap-2 text-sm text-white/40">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          Aguardando pagamento...
        </div>

        {paymentId && (
          <p className="text-center text-[10px] text-white/20 mt-2">
            ID: {paymentId}
          </p>
        )}
      </div>
    )
  }

  // ========================================
  // RENDER: Step Success
  // ========================================

  if (step === 'success') {
    return (
      <div className="bg-zinc-900 rounded-2xl border border-green-500/30 p-8">
        <div className="text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-green-500/30">
            <span className="text-4xl">&#x1F389;</span>
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Premium ativado!</h3>
          <p className="text-sm text-white/50">
            Todas as categorias foram desbloqueadas. Bons estudos!
          </p>
        </div>
      </div>
    )
  }

  // ========================================
  // RENDER: Step Error
  // ========================================

  return (
    <div className="bg-zinc-900 rounded-2xl border border-red-500/30 p-6">
      <div className="text-center mb-4">
        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-3 border border-red-500/30">
          <span className="text-3xl">&#x26A0;</span>
        </div>
        <h3 className="text-lg font-bold text-white mb-1">Erro no pagamento</h3>
        <p className="text-sm text-red-400/80">{error || 'Ocorreu um erro inesperado'}</p>
      </div>
      <div className="flex gap-2">
        <button
          onClick={onBack}
          className="flex-1 py-3 rounded-xl bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors"
        >
          Voltar
        </button>
        <button
          onClick={() => {
            setError(null)
            setStep('cpf')
          }}
          className="flex-1 py-3 rounded-xl bg-gradient-to-r from-green-500 to-cyan-500 text-black text-sm font-bold hover:from-green-400 hover:to-cyan-400 transition-all"
        >
          Tentar novamente
        </button>
      </div>
    </div>
  )
}
